<script setup lang="ts" generic="TData">
  import type { Table } from '@tanstack/vue-table'
  import GatButton from '../gat-button/gat-button.vue'
  import GatIcon from '../gat-icon/gat-icon.vue'
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
  } from '../ui/select'

  export interface DataTablePaginationProps<TData> {
    table: Table<TData>
  }

  const props = defineProps<DataTablePaginationProps<TData>>()
</script>

<template>
  <div class="flex items-center justify-between px-2">
    <div class="text-soft flex-1 text-sm">
      {{ table.getFilteredSelectedRowModel().rows.length }} of
      {{ table.getFilteredRowModel().rows.length }} row(s) selected.
    </div>
    <div class="flex items-center space-x-6 lg:space-x-8">
      <div class="flex items-center space-x-2">
        <p class="text-sm font-medium">Rows per page</p>
        <!-- @ts-ignore -->
        <Select
          :model-value="table.getState().pagination.pageSize"
          @update:model-value="(value) => table.setPageSize(Number(value))"
        >
          <SelectTrigger class="h-8 w-[70px]">
            <SelectValue
              :placeholder="String(table.getState().pagination.pageSize)"
            />
          </SelectTrigger>
          <SelectContent side="top">
            <SelectItem
              v-for="pageSize in [10, 20, 30, 40, 50]"
              :key="pageSize"
              :value="`${pageSize}`"
            >
              {{ pageSize }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div
        class="flex w-[100px] items-center justify-center text-sm font-medium"
      >
        Page {{ table.getState().pagination.pageIndex + 1 }} of
        {{ table.getPageCount() }}
      </div>
      <div class="flex items-center space-x-2">
        <GatButton
          variant="outline"
          class="hidden h-8 w-8 p-0 lg:flex"
          :disabled="!table.getCanPreviousPage()"
          @click="table.setPageIndex(0)"
        >
          <span class="sr-only">Go to first page</span>
          <gat-icon icon="ChevronsLeft" class="h-4 w-4" />
        </GatButton>
        <GatButton
          variant="outline"
          class="h-8 w-8 p-0"
          :disabled="!table.getCanPreviousPage()"
          @click="table.previousPage()"
        >
          <span class="sr-only">Go to previous page</span>
          <gat-icon icon="ChevronLeft" class="h-4 w-4" />
        </GatButton>
        <GatButton
          variant="outline"
          class="h-8 w-8 p-0"
          :disabled="!table.getCanNextPage()"
          @click="table.nextPage()"
        >
          <span class="sr-only">Go to next page</span>
          <gat-icon icon="ChevronRight" class="h-4 w-4" />
        </GatButton>
        <GatButton
          variant="outline"
          class="hidden h-8 w-8 p-0 lg:flex"
          :disabled="!table.getCanNextPage()"
          @click="table.setPageIndex(table.getPageCount() - 1)"
        >
          <span class="sr-only">Go to last page</span>
          <gat-icon icon="ChevronsRight" class="h-4 w-4" />
        </GatButton>
      </div>
    </div>
  </div>
</template>
